/**
 * DisputeResourcesSection — home-page block that gives landlords and
 * tenants a clear resolution framework when a dispute happens. Two card
 * columns (Landlord / Tenant), each listing common problems and the
 * concrete action steps to resolve them.
 *
 * SEO angle: ranks on "landlord tenant dispute Canada", "how to file N4
 * Ontario", "how to withhold rent BC", etc. Every card links to a
 * dedicated tool or answer page so it also passes internal PageRank.
 */
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const LANDLORD_ISSUES = [
  {
    title: "Tenant hasn't paid rent",
    steps: [
      "Send a friendly reminder inside Central Rentals",
      "After 14 days, generate an Ontario N4 or provincial equivalent",
      "File with the LTB / RTB / TAL if arrears aren't cured",
    ],
    tool: { label: "N4 Generator", to: "/tools/n4-late-rent-notice" },
    testid: "dispute-landlord-arrears",
  },
  {
    title: "Property damage beyond wear and tear",
    steps: [
      "Photograph & date-stamp every damaged item",
      "Deduct from the security deposit with an itemized statement",
      "If cost exceeds the deposit, file a small-claims application",
    ],
    tool: { label: "Damage checklist", to: "/answers" },
    testid: "dispute-landlord-damage",
  },
  {
    title: "Illegal sublet or unauthorized occupants",
    steps: [
      "Send a formal N5 (or provincial equivalent) demanding compliance",
      "Give the tenant 7 days to remove the additional occupants",
      "If unresolved, file for termination with the tribunal",
    ],
    tool: { label: "N5 Generator", to: "/tools" },
    testid: "dispute-landlord-sublet",
  },
  {
    title: "Rent increase is being disputed",
    steps: [
      "Confirm the notice period (usually 90 days) & provincial cap",
      "Reissue on the correct provincial form (N1 in Ontario, BC RTB-7)",
      "If the tenant refuses, file a hearing request at the tribunal",
    ],
    tool: { label: "Rent-Increase Tool", to: "/tools/rent-increase-notice" },
    testid: "dispute-landlord-increase",
  },
];

const TENANT_ISSUES = [
  {
    title: "Landlord won't return your deposit",
    steps: [
      "Send a written demand letter with your forwarding address",
      "Wait the provincial return window (14-21 days)",
      "If no reply, file at the RTB / LTB / TAL — it's usually free",
    ],
    tool: { label: "Deposit Return Kit", to: "/answers" },
    testid: "dispute-tenant-deposit",
  },
  {
    title: "Illegal rent increase notice received",
    steps: [
      "Check the notice period and provincial cap on our calculator",
      "Reply in writing citing the correct rule — you don't owe the extra",
      "File a T1 (Ontario) or provincial complaint if pressure continues",
    ],
    tool: { label: "Rent-Cap Checker", to: "/tools/rent-increase-notice" },
    testid: "dispute-tenant-illegal-increase",
  },
  {
    title: "Repairs the landlord keeps ignoring",
    steps: [
      "Send a written repair request — text or email counts",
      "Wait a reasonable time (7-14 days depending on urgency)",
      "File a maintenance order at the tribunal or municipal by-law office",
    ],
    tool: { label: "Repair-Request Letter", to: "/answers" },
    testid: "dispute-tenant-repairs",
  },
  {
    title: "You received an eviction notice",
    steps: [
      "Don't ignore it — the deadline is real and enforceable",
      "Check that the notice form matches your province (N4/N12/TAL)",
      "Attend the tribunal hearing with photos, receipts, texts and witnesses",
    ],
    tool: { label: "Notice Decoder", to: "/answers" },
    testid: "dispute-tenant-eviction",
  },
];


function IssueCard({ item }) {
  return (
    <Link
      to={item.tool.to}
      data-testid={item.testid}
      className="group block rounded-2xl bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all p-5"
    >
      <div className="font-display text-base sm:text-lg font-black text-slate-900">
        {item.title}
      </div>
      <ol className="mt-3 space-y-1.5 text-sm text-slate-700">
        {item.steps.map((s, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-emerald-700 font-bold tabular-nums">{i + 1}.</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-emerald-700 group-hover:gap-2 transition-all">
        {item.tool.label}
        <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}


export default function DisputeResourcesSection() {
  return (
    <section
      className="mt-14 border-t border-slate-100"
      data-testid="dispute-resources-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-emerald-700">
          Resolve a dispute
        </div>
        <h2 className="font-display mt-2 text-3xl sm:text-4xl font-extrabold text-slate-950 leading-tight max-w-3xl">
          Landlord vs Tenant, sorted in three steps.
        </h2>
        <p className="mt-3 text-slate-700 max-w-2xl">
          Every province gives both sides a clear resolution process. Below is the
          Central Rentals quick-reference guide — the eight most common disputes
          we see, with the exact action to take next. Every step links to the
          tool, notice generator or answer page that resolves it.
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-500 mb-4">
              If you're the landlord
            </div>
            <div className="space-y-4">
              {LANDLORD_ISSUES.map((it) => (
                <IssueCard key={it.testid} item={it} />
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-500 mb-4">
              If you're the tenant
            </div>
            <div className="space-y-4">
              {TENANT_ISSUES.map((it) => (
                <IssueCard key={it.testid} item={it} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/answers"
            data-testid="dispute-resources-see-all"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-950 hover:bg-slate-800 text-white text-sm font-extrabold transition-colors"
          >
            See every provincial answer
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
